import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getLocalDiscogsList, type DiscogsListData } from '../api/discogs'
import { useDiscogsConnection } from '../context/DiscogsContext'
import { useAuth } from '../context/AuthContext'

/**
 * Server-state for the Discogs collection & wantlist.
 *
 * The actual list data (which release IDs are in the collection / wantlist and
 * when they were added) is fetched from the local DB mirror and cached in
 * React Query — NOT in DiscogsContext. This keeps sync-progress ticks (which
 * live in DiscogsContext) from re-rendering pages that only care about list
 * membership.
 *
 * Heavy sync orchestration (page-by-page Discogs fetch + cleanup) stays in
 * DiscogsContext; when it finishes it writes the fresh data straight into this
 * cache via `setDiscogsListData`, so the two stay in lock-step.
 */

const WANTLIST_STORAGE_KEY = 'discogs_wantlist'
const COLLECTION_IDS_STORAGE_KEY = 'discogs_collection_ids'
const COLLECTION_DATES_KEY = 'discogs_collection_dates'
const WANTLIST_DATES_KEY = 'discogs_wantlist_dates'

const EMPTY: DiscogsListData = { ids: [], dates: {} }

export const discogsKeys = {
  all: ['discogs'] as const,
  collection: () => [...discogsKeys.all, 'collection'] as const,
  wantlist: () => [...discogsKeys.all, 'wantlist'] as const,
}

type ListName = 'collection' | 'wantlist'

function storageKeysFor(list: ListName, userId?: number) {
  const suffix = userId ? `:${userId}` : ''
  return list === 'collection'
    ? { ids: `${COLLECTION_IDS_STORAGE_KEY}${suffix}`, dates: `${COLLECTION_DATES_KEY}${suffix}` }
    : { ids: `${WANTLIST_STORAGE_KEY}${suffix}`, dates: `${WANTLIST_DATES_KEY}${suffix}` }
}

function queryKeyFor(list: ListName) {
  return list === 'collection' ? discogsKeys.collection() : discogsKeys.wantlist()
}

/** Read the last-known list from localStorage so the UI hydrates instantly. */
function readCachedList(list: ListName, userId?: number): DiscogsListData {
  const { ids, dates } = storageKeysFor(list, userId)
  try {
    const storedIds = localStorage.getItem(ids)
    const storedDates = localStorage.getItem(dates)
    return {
      ids: storedIds ? JSON.parse(storedIds) : [],
      dates: storedDates ? JSON.parse(storedDates) : {},
    }
  } catch {
    return EMPTY
  }
}

/** Persist the list to localStorage to preserve the offline/instant-load path. */
function persistList(list: ListName, data: DiscogsListData, userId?: number) {
  const { ids, dates } = storageKeysFor(list, userId)
  try {
    if (data.ids.length > 0) localStorage.setItem(ids, JSON.stringify(data.ids))
    else localStorage.removeItem(ids)
    if (Object.keys(data.dates).length > 0) localStorage.setItem(dates, JSON.stringify(data.dates))
    else localStorage.removeItem(dates)
  } catch {
    /* storage may be full / disabled — in-memory cache is still fine */
  }
}

function useDiscogsList(list: ListName) {
  const { isAuthenticated } = useDiscogsConnection()
  const { user } = useAuth()
  return useQuery<DiscogsListData>({
    queryKey: queryKeyFor(list),
    queryFn: async () => {
      const data = await getLocalDiscogsList(list)
      persistList(list, data, user?.id)
      return data
    },
    enabled: isAuthenticated,
    // Instant hydration from the last-synced snapshot in localStorage.
    // Do not seed a disabled guest query with an empty value: React Query would
    // consider it fresh when Discogs authentication completes and skip the
    // initial local-DB fetch after a normal in-app login.
    initialData: isAuthenticated ? () => readCachedList(list, user?.id) : undefined,
    // The local DB only changes after an explicit sync or a membership write,
    // both of which update the cache directly — so a long staleTime avoids
    // redundant refetches while keeping the data correct.
    staleTime: 1000 * 60 * 5,
  })
}

/** React Query hook for the Discogs collection (IDs + added dates). */
export function useCollection() {
  const query = useDiscogsList('collection')
  const data = query.data ?? EMPTY
  return {
    ...query,
    ids: data.ids,
    dates: data.dates,
  }
}

/** React Query hook for the Discogs wantlist (IDs + added dates). */
export function useWantlist() {
  const query = useDiscogsList('wantlist')
  const data = query.data ?? EMPTY
  return {
    ...query,
    ids: data.ids,
    dates: data.dates,
  }
}

/**
 * Imperative cache accessor used by the sync orchestration in DiscogsContext
 * and by membership writes. Lets callers read, replace, or surgically mutate
 * the cached list without forcing a network refetch.
 */
export function useDiscogsCache() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const setListData = useCallback(
    (list: ListName, data: DiscogsListData) => {
      persistList(list, data, user?.id)
      queryClient.setQueryData(queryKeyFor(list), data)
    },
    [queryClient, user?.id],
  )

  const clearAll = useCallback(() => {
    persistList('collection', EMPTY, user?.id)
    persistList('wantlist', EMPTY, user?.id)
    queryClient.setQueryData(discogsKeys.collection(), EMPTY)
    queryClient.setQueryData(discogsKeys.wantlist(), EMPTY)
  }, [queryClient, user?.id])

  const addId = useCallback(
    (list: ListName, releaseId: number) => {
      queryClient.setQueryData<DiscogsListData>(queryKeyFor(list), (prev) => {
        const base = prev ?? EMPTY
        if (base.ids.includes(releaseId)) return base
        const next = { ids: [...base.ids, releaseId], dates: base.dates }
        persistList(list, next, user?.id)
        return next
      })
    },
    [queryClient, user?.id],
  )

  const removeId = useCallback(
    (list: ListName, releaseId: number) => {
      queryClient.setQueryData<DiscogsListData>(queryKeyFor(list), (prev) => {
        const base = prev ?? EMPTY
        if (!base.ids.includes(releaseId)) return base
        const dates = { ...base.dates }
        delete dates[releaseId]
        const next = { ids: base.ids.filter((id) => id !== releaseId), dates }
        persistList(list, next, user?.id)
        return next
      })
    },
    [queryClient, user?.id],
  )

  return { setListData, clearAll, addId, removeId }
}

/**
 * Membership helpers + optimistic mutators for collection/wantlist.
 *
 * Replaces the old context-state mutators (`addToWantlist`, `isInCollection`, …)
 * so call-sites keep an identical API while the underlying data now lives in
 * the React Query cache.
 */
export function useDiscogsMembership() {
  const { ids: wantlistIds } = useWantlist()
  const { ids: collectionIds } = useCollection()
  const { addId, removeId } = useDiscogsCache()

  const wantlistSet = new Set(wantlistIds)
  const collectionSet = new Set(collectionIds)

  return {
    isInWantlist: useCallback((id: number) => wantlistSet.has(id), [wantlistIds]), // eslint-disable-line react-hooks/exhaustive-deps
    isInCollection: useCallback((id: number) => collectionSet.has(id), [collectionIds]), // eslint-disable-line react-hooks/exhaustive-deps
    addToWantlist: useCallback((id: number) => addId('wantlist', id), [addId]),
    removeFromWantlist: useCallback((id: number) => removeId('wantlist', id), [removeId]),
    addToCollection: useCallback((id: number) => addId('collection', id), [addId]),
    removeFromCollection: useCallback((id: number) => removeId('collection', id), [removeId]),
  }
}
